import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import { AppBarWithButtons } from '../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppDashedBlockButton from '../../../components/designe/buttons/block-buttons/AppDashedBlockButton';
import AppFormSelectWrapper from '../../../components/designe/forms/app-form-select-wrapper';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import SavedProgramSetSuperset from '../../../components/reusable/saved-program-set-superset-modal/SavedProgramSetSuperset';
import SetGroupEditTable from '../../../components/reusable/set-group-edit-table/SetGroupEditTable';
import withRouter from '../../../hoc/with-router';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { groupBy } from '../../../utils/collections/array-utils';
import { getSetKey } from '../../../utils/training/set-utils';
import { useStyles } from './EditProgramItem.styles';
import { useEditProgramItemFormik } from './useEditProgramItemFormik';
import { useEditProgramItemInit } from './useEditProgramItemInit';
import useEditTable from './useEditTable';

function EditProgramItem(props) {
  const { params } = props;
  const { t } = useTranslation();
  const { isSaving, programItem, initialValues, onSubmit } =
    useEditProgramItemInit(params.programItemId);

  const {
    formik,
    programCollectionMap,
    onAddEmptyProgramSet,
    onAddSavedProgramSet,
    onCancelClick,
    onChangeSetOrder,
    showSavedProgramItemModal,
    setShowSavedProgramItemModal,
  } = useEditProgramItemFormik(params.programItemId, initialValues, onSubmit);
  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction: onCancelClick,
  });
  const { showUploadSetButton } = useUserSetPreferences();
  const useSetGroupEditTable = useEditTable(formik, params.programItemId);

  useScrollToFirstError(formik, {
    fieldOrder: ['title', 'programSets'],
  });

  const theme = useTheme();

  const { classes } = useStyles(theme);

  const programSetGroups = useMemo(() => {
    return formik.values.programSets
      ? groupBy(formik.values.programSets, 'setNumber')
      : null;
  }, [formik.values.programSets]);

  const setIndexMap = useMemo(() => {
    let acc = 0;
    return programSetGroups
      ? Object.values(programSetGroups).map((group) => {
          const current = acc;
          acc += group?.length;
          return current;
        })
      : acc;
  }, [programSetGroups]);

  return (
    <PageContainer
      title={`${params.programItemId ? `${t('programs.programItem')}: ${programItem?.title}` : t('programs.newProgramItem')}`}
      backButton>
      <Box className={classes.programItemWrapper}>
        {dialog}
        <form
          onSubmit={formik.handleSubmit}
          className={classes.programItemForm}>
          <Box className={classes.infoDataWrapper}>
            <Box>
              <AppFormTextField
                label={t('common.title')}
                fullWidth
                autoFocus
                name="title"
                formik={formik}
              />
            </Box>
            <Box marginTop={1}>
              <AppFormTextField
                label={t('common.description')}
                fullWidth
                multiline
                minRows={3}
                maxRows={15}
                name="description"
                formik={formik}
              />
            </Box>
            <Box marginTop={1}>
              <AppFormSelectWrapper
                data-onboarding="newTraining.collectionSelector"
                label={t('programs.collection')}
                options={programCollectionMap}
                onChange={(e) =>
                  formik.setFieldValue(
                    'programCollectionId',
                    e?.target?.value?.id,
                  )
                }
                name={'programCollectionId'}
                formik={formik}
              />
            </Box>
          </Box>
          <Box
            className={classes.programSetsContainer}
            data-formik-field="programSets">
            {programSetGroups &&
              Object.values(programSetGroups).map(
                (programSetGroup, index: number) => (
                  <Box
                    className={classes.programSetWrapper}
                    key={getSetKey(programSetGroup[0])}>
                    <SetGroupEditTable
                      useSetGroupEditTable={useSetGroupEditTable}
                      sets={programSetGroup}
                    />
                  </Box>
                ),
              )}
          </Box>

          <Box className={classes.addProgramSetFooter}>
            <AppDashedBlockButton
              data-onboarding="newTraining.addTrainingSet"
              fullWidth
              onClick={() => onAddEmptyProgramSet()}
              startIcon={<AddIcon />}
              yPadding={1}>
              {t('programs.addSetToProgram')}
            </AppDashedBlockButton>
            {showUploadSetButton && (
              <AppDashedBlockButton
                fullWidth
                onClick={() => setShowSavedProgramItemModal(true)}
                startIcon={<FileUploadIcon />}
                yPadding={1}>
                {t('programs.loadSetIntoProgram')}
              </AppDashedBlockButton>
            )}
          </Box>

          <div className={classes.programItemFooter}>
            <AppBarWithButtons
              loading={isSaving}
              onCancelClick={allowAndLeave}
              submitButtonProps={{ 'data-onboarding': 'newTraining.save' }}
            />
          </div>
        </form>
        {showUploadSetButton && (
          <AppDialog
            open={!!showSavedProgramItemModal}
            onClose={() => setShowSavedProgramItemModal(false)}
            title={t('programs.savedProgramSets')}>
            <SavedProgramSetSuperset
              onCancel={() => setShowSavedProgramItemModal(false)}
              onSubmitSavedProgramSet={(value: {
                programSets: IProgramSet[];
                isSuperset: boolean;
              }) => {
                setShowSavedProgramItemModal(false);
                onAddSavedProgramSet(value.programSets, value.isSuperset);
              }}
            />
          </AppDialog>
        )}
      </Box>
    </PageContainer>
  );
}

export default withRouter(EditProgramItem);
